/**
 * Constants and types for BlueYonder integration
 */

import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

// Log stamp for consistent logging across the application
export const BLUE_YONDER_LOG_STAMP = "- BlueYonder Integration -";

// BlueYonder locations URL for customers to find drop-off points
export const BLUE_YONDER_LOCATIONS_URL = "https://www.blueyonder.com/locations";

// BlueYonder webhook event types for processing
export enum WebhookEventType {
  DROPPED_OFF = "RETURN_EVENT-DROPPED_OFF",
  DESPATCHED_FROM_PUDO = "RETURN_EVENT-DESPATCHED_FROM_PUDO",
  SCANNED_TO_SORT_CONTAINER = "RETURN_EVENT-SCANNED_TO_SORT_CONTAINER",
  ON_ITS_WAY = "RETURN_EVENT-ON_ITS_WAY",
  ARRIVED_AT_DESTINATION = "RETURN_EVENT-ARRIVED_AT_DESTINATION",
}

export const blueYonderWebhookEventTypeMessages: Record<
  WebhookEventType,
  string
> = {
  [WebhookEventType.DROPPED_OFF]: "Package dropped off at FedEx location",
  [WebhookEventType.DESPATCHED_FROM_PUDO]:
    "Package dispatched from FedEx pickup/drop-off center",
  [WebhookEventType.SCANNED_TO_SORT_CONTAINER]:
    "Package scanned to sort container",
  [WebhookEventType.ON_ITS_WAY]: "Package on its way",
  [WebhookEventType.ARRIVED_AT_DESTINATION]: "Package arrived at destination",
};

export const blueYonderLocationSchema = z.object({
  blueyonderLocationId: z.string(),
  locationName: z.string(),
});

export const blueYonderIntegrationInputSchema = z.object({
  retailerCompanyId: z.string(),
  locations: z.array(blueYonderLocationSchema).optional(),
  processingEvent: createEnum(WebhookEventType).optional(),
  testMode: z.boolean().optional(),
  radiusMiles: z.number().min(1).optional(),
  upcharge: z.number().optional(),
  title: z.string().optional(),
  subtitle: z.string().optional(),
  locationsText: z.string().optional(),
});

export type BlueYonderIntegrationInput = z.infer<
  typeof blueYonderIntegrationInputSchema
>;

export enum BlueYonderIssueCode {
  NOT_HEADED_TO_BLUEYONDER = "not-headed-to-blueyonder",
  MISSING_SKUS = "missing-skus",
  PENDING_MANUAL_REVIEW = "pending-manual-review",
  NO_SHIPMENTS_PRESENT = "no-shipments-present",
  INTERNAL_ERROR = "internal-error",
  SUCCESS = "success",
}

export const blueYonderIssueCodeToDisplayText: Record<
  BlueYonderIssueCode,
  string
> = {
  [BlueYonderIssueCode.NOT_HEADED_TO_BLUEYONDER]: "not headed to BlueYonder",
  [BlueYonderIssueCode.MISSING_SKUS]: "missing SKUs",
  [BlueYonderIssueCode.PENDING_MANUAL_REVIEW]: "pending manual review",
  [BlueYonderIssueCode.NO_SHIPMENTS_PRESENT]: "no shipments present",
  [BlueYonderIssueCode.INTERNAL_ERROR]: "internal error",
  [BlueYonderIssueCode.SUCCESS]: "success",
};

export enum DropoffReturnStatus {
  CREATED = "created",
  DROPPED_OFF = "RETURN_EVENT-DROPPED_OFF",
  DESPATCHED_FROM_PUDO = "RETURN_EVENT-DESPATCHED_FROM_PUDO",
  SCANNED_TO_SORT_CONTAINER = "RETURN_EVENT-SCANNED_TO_SORT_CONTAINER",
  ON_ITS_WAY = "RETURN_EVENT-ON_ITS_WAY",
  ARRIVED_AT_DESTINATION = "RETURN_EVENT-ARRIVED_AT_DESTINATION",
}

export const dropoffReturnStatusToDisplayText: Record<
  DropoffReturnStatus,
  string
> = {
  [DropoffReturnStatus.CREATED]: "Awaiting drop-off",
  [DropoffReturnStatus.DROPPED_OFF]: "Dropped off",
  [DropoffReturnStatus.DESPATCHED_FROM_PUDO]: "Dispatched",
  [DropoffReturnStatus.SCANNED_TO_SORT_CONTAINER]: "Scanned to sort container",
  [DropoffReturnStatus.ON_ITS_WAY]: "On its way",
  [DropoffReturnStatus.ARRIVED_AT_DESTINATION]: "Arrived",
};

/**
 * Ordered progression of webhook events from earliest to latest.
 * Used to determine if a received event is "on or after" a configured processing event.
 */
export const WEBHOOK_EVENT_ORDER: readonly WebhookEventType[] = [
  WebhookEventType.DROPPED_OFF,
  WebhookEventType.DESPATCHED_FROM_PUDO,
  WebhookEventType.SCANNED_TO_SORT_CONTAINER,
  WebhookEventType.ON_ITS_WAY,
  WebhookEventType.ARRIVED_AT_DESTINATION,
];

/**
 * Returns true if `receivedEvent` is at or after `targetEvent` in the webhook event progression.
 * For example, if target is DROPPED_OFF and received is ON_ITS_WAY, returns true
 * because the drop-off must have already occurred.
 */
export function isEventOnOrAfter(
  targetEvent: WebhookEventType,
  receivedEvent: WebhookEventType,
): boolean {
  const targetIndex = WEBHOOK_EVENT_ORDER.indexOf(targetEvent);
  const receivedIndex = WEBHOOK_EVENT_ORDER.indexOf(receivedEvent);
  return receivedIndex >= targetIndex;
}

export const BLUEYONDER_DATADOG_METRIC = "redo.return.blueyonder.createRma";

export enum BlueYonderMetricTags {
  OUTCOME = "redo.return.blueyonder.outcome",
  NUM_SHIPMENT_GROUPS_PROCESSED = "redo.return.blueyonder.numShipmentGroupsProcessed",
}
